/**
 * Joey's own ratings of the cards she receives (#55), and the sums built on them.
 *
 * Everything here is owner-only. The server leaves `ownerRating` off every card for a
 * visitor (models.Public), so a card without one is simply a card nobody signed in is
 * looking at, and nothing here should ever be shown in its place.
 *
 * A rating is 0 to 3, or null for "not rated yet". 0 means "low effort" and is a real
 * rating that counts toward an average; null never does. Keeping those two apart is the
 * whole reason `rating` is nullable rather than defaulting to 0.
 */
import { senderKey } from "./sender-runs";

export type OwnerRating = {
  rating: number | null;
  tags: string[];
  leaveUnrated: boolean;
};

export type RatingTag = {
  key: string;
  label: string;
  /** Said in smaller type after the label, where the label alone would be misread. */
  note?: string;
  good: boolean;
};

/**
 * The fixed list, from Joey's comment on #55. The keys must match models.RatingTagKeys,
 * which is what the server accepts; the labels and grouping live only here.
 */
export const RATING_TAGS: readonly RatingTag[] = [
  { key: "wrote-a-lot", label: "Wrote a lot", good: true },
  { key: "thoughtful-message", label: "Thoughtful message", good: true },
  { key: "nice-stamps", label: "Nice stamps", good: true },
  { key: "decoration", label: "Cool stickers / washi / decoration", good: true },
  { key: "nice-handwriting", label: "Nice handwriting", good: true },
  // "No words" is for a card with nothing written that was not a forum request. A sticker
  // tag card, where covering the back is the point, should not get it — hence the note.
  { key: "no-words", label: "No words", note: "not forum-requested", good: false },
  { key: "generic-greeting", label: "Generic greeting", good: false },
  { key: "inappropriate-content", label: "Inappropriate / non-preferred content", good: false },
];

export const GOOD_TAGS = RATING_TAGS.filter((tag) => tag.good);
export const NOT_SO_GOOD_TAGS = RATING_TAGS.filter((tag) => !tag.good);

const tagsByKey = new Map(RATING_TAGS.map((tag) => [tag.key, tag]));
const tagOrder = new Map(RATING_TAGS.map((tag, index) => [tag.key, index]));

/**
 * The tag a stored key names, or undefined for a key not on the list — which callers skip
 * rather than show, so a retired key can never render as a blank chip.
 */
export function ratingTag(key: string): RatingTag | undefined {
  return tagsByKey.get(key);
}

/** The fields the sums need. Home.tsx and the ratings page both pass whole cards. */
export type RatedCard = {
  id: string;
  senderUser: string;
  originCountry: string;
  receivedDate: string;
  createdAt: string;
  ownerRating?: OwnerRating;
};

/** A card's rating, with "no ownerRating at all" read as unrated (null), never as 0. */
export function ratingOf(card: RatedCard): number | null {
  return card.ownerRating?.rating ?? null;
}

export type Band = "good" | "mixed" | "low";

export const VERDICTS: Record<Band, string> = {
  good: "Usually puts in real effort",
  mixed: "Mixed",
  low: "Usually low effort",
};

/**
 * The average as it is shown: one decimal. Banding goes through this rather than the raw
 * mean, so a sender shown as "2.0 / 3 ★" can never be called "Mixed" because their true
 * average was 1.96.
 */
export function roundedAverage(average: number) {
  return Math.round(average * 10) / 10;
}

/**
 * Which verdict an average earns: 2 and up is "Usually puts in real effort", 1 and up is
 * "Mixed", below that "Usually low effort". Judged on the shown average; see above.
 */
export function band(average: number): Band {
  const shown = roundedAverage(average);
  if (shown >= 2) return "good";
  if (shown >= 1) return "mixed";
  return "low";
}

/** An average as the page writes it, e.g. "2.3 / 3 ★". */
export function formatAverage(average: number) {
  return `${roundedAverage(average).toFixed(1)} / 3 ★`;
}

/** How many of each tag, most-used first; ties keep the list's own order. */
export function tagCounts(cards: readonly RatedCard[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const card of cards) {
    for (const tag of card.ownerRating?.tags ?? []) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) =>
    b[1] - a[1] || (tagOrder.get(a[0]) ?? 99) - (tagOrder.get(b[0]) ?? 99));
}

/** Newest first, the gallery's default order (received date, then when it was entered). */
export function newestFirst(a: { receivedDate: string; createdAt: string }, b: { receivedDate: string; createdAt: string }) {
  return b.receivedDate.localeCompare(a.receivedDate) || b.createdAt.localeCompare(a.createdAt);
}

/**
 * The rating sums over a set of cards: how many are rated, their mean, and how many at
 * each rating. Unrated cards count toward none of it; 0 counts as a real rating. The
 * sender list and the lookup's per-thread rows both average through this, so the two
 * can never disagree about what an average means.
 */
export function ratingStats(cards: readonly RatedCard[]) {
  const spread: [number, number, number, number] = [0, 0, 0, 0];
  let total = 0;
  let rated = 0;
  for (const card of cards) {
    const rating = ratingOf(card);
    if (rating === null) continue;
    spread[rating] += 1;
    total += rating;
    rated += 1;
  }
  return { rated, unrated: cards.length - rated, average: rated ? total / rated : null, spread };
}

export type SenderSummary<C extends RatedCard = RatedCard> = {
  /** senderKey of the name: the grouping the gallery's postmarks and Senders stat use. */
  key: string;
  /** Spelled as on their most recent card. */
  name: string;
  country: string;
  /** Every card from them, rated or not, newest first. */
  cards: C[];
  rated: number;
  unrated: number;
  /** Mean of the rated cards only; null when none is rated yet. */
  average: number | null;
  /** How many cards at each rating, index 0 to 3. */
  spread: [number, number, number, number];
  /** Latest received date across all their cards, as written ("" if none has one). */
  lastReceived: string;
  tags: [string, number][];
};

/**
 * One summary per person.
 *
 * Grouped by senderKey, the same fold the gallery uses for its postmarks and its Senders
 * count — a name typed once with a trailing space is still one person here, and the
 * ratings page cannot disagree with the gallery about how many people have written.
 */
export function senderSummaries<C extends RatedCard>(cards: readonly C[]): SenderSummary<C>[] {
  const groups = new Map<string, C[]>();
  for (const card of cards) {
    const key = senderKey(card.senderUser);
    if (!key) continue;
    const group = groups.get(key);
    if (group) group.push(card);
    else groups.set(key, [card]);
  }
  return [...groups.entries()].map(([key, group]) => {
    const sorted = [...group].sort(newestFirst);
    const { rated, unrated, average, spread } = ratingStats(sorted);
    const lastReceived = sorted.reduce((latest, card) =>
      card.receivedDate.localeCompare(latest) > 0 ? card.receivedDate : latest, "");
    return {
      key,
      name: sorted[0].senderUser.trim(),
      country: sorted[0].originCountry,
      cards: sorted,
      rated,
      unrated,
      average,
      spread,
      lastReceived,
      tags: tagCounts(sorted),
    };
  });
}

/**
 * The sender a typed username names, or the near misses if there is no exact one.
 *
 * A leading "@" is dropped, because that is how usernames get written, and the rest goes
 * through senderKey so the lookup finds exactly the person the gallery would.
 */
export function lookUpSender<C extends RatedCard>(summaries: readonly SenderSummary<C>[], typed: string) {
  const key = senderKey(typed.trim().replace(/^@+/, ""));
  if (!key) return { key, match: undefined, near: [] as SenderSummary<C>[] };
  const match = summaries.find((summary) => summary.key === key);
  const near = match ? [] : summaries.filter((summary) => summary.key.includes(key)).slice(0, 6);
  return { key, match, near };
}

export type SenderFilter = "all" | Band;
export type SenderSort = "name" | "average" | "cards" | "last";

/**
 * The rows of the sender list: people with at least `minRated` rated cards, in a band if
 * one is chosen, sorted by the chosen column. "Cards" is the rated count, since that is
 * what the average stands on.
 */
export function senderRows<C extends RatedCard>(
  summaries: readonly SenderSummary<C>[],
  { filter, minRated, sort, descending }: { filter: SenderFilter; minRated: number; sort: SenderSort; descending: boolean },
) {
  const rows = summaries.filter((summary) =>
    summary.average !== null && summary.rated >= minRated && (filter === "all" || band(summary.average) === filter));
  const direction = descending ? -1 : 1;
  return rows.sort((a, b) => {
    const byName = a.key.localeCompare(b.key);
    const order = sort === "average" ? (a.average ?? 0) - (b.average ?? 0)
      : sort === "cards" ? a.rated - b.rated
      : sort === "last" ? a.lastReceived.localeCompare(b.lastReceived)
      : byName;
    // Name breaks every tie, always A–Z, so equal averages do not shuffle between renders.
    return order * direction || byName;
  });
}

/** Cards waiting for a rating: not rated, and not set aside with "Leave unrated". */
export function awaitingRating<C extends RatedCard>(cards: readonly C[]): C[] {
  return cards.filter((card) => card.ownerRating && card.ownerRating.rating === null && !card.ownerRating.leaveUnrated);
}

/** One quick-rate row as it stands before it is saved. */
export type QuickRateRow = { rating: number | null; tags: string[]; tagsOpen: boolean };

/**
 * Where a quick-rate row starts: no stars yet, and the tags the card already has.
 *
 * The row's tags are what gets saved, and the save replaces the card's tags wholesale. So
 * a card tagged in the add form but left without stars must start with those tags; a row
 * that started empty would erase them with one tap on the stars or on "Leave unrated".
 */
export function quickRateRow(card: RatedCard): QuickRateRow {
  return { rating: null, tags: [...(card.ownerRating?.tags ?? [])], tagsOpen: false };
}

/**
 * What a quick-rate row sends when it is finished: a rating from the stars or Done, or
 * null for "Leave unrated", which is the only thing that sets the leave-unrated flag. The
 * row's tags go with it either way.
 */
export function quickRateSave(row: QuickRateRow, rating: number | null): OwnerRating {
  return { rating, tags: [...row.tags], leaveUnrated: rating === null };
}

/** How far back "Recently added" reaches, by when a card was entered. */
export const RECENT_DAYS = 30;

/**
 * created_at comes from SQLite's CURRENT_TIMESTAMP: "YYYY-MM-DD HH:MM:SS" in UTC with no
 * zone marker. Read as UTC; anything unreadable counts as old rather than as new.
 */
function enteredAt(createdAt: string) {
  const iso = createdAt.includes("T") ? createdAt : `${createdAt.replace(" ", "T")}Z`;
  const time = Date.parse(iso);
  return Number.isNaN(time) ? 0 : time;
}

/**
 * The two halves of the quick-rate list.
 *
 * "Recently added" is by entry date, not received date: received dates are often
 * backdated, and a card entered this week is one she has just had in her hands whatever
 * its postmark says. Newest entered first. "Older cards" is everything else, newest
 * received first, so working down it goes back through the collection in order.
 */
export function quickRateLists<C extends RatedCard>(cards: readonly C[], now: Date = new Date()) {
  const cutoff = now.getTime() - RECENT_DAYS * 86_400_000;
  const waiting = awaitingRating(cards);
  const recent = waiting.filter((card) => enteredAt(card.createdAt) >= cutoff)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id));
  const older = waiting.filter((card) => enteredAt(card.createdAt) < cutoff).sort(newestFirst);
  return { recent, older };
}
